// bridge-core.js — window.OPDBridge for the Vite SPA.
//
// Ported from frontend/shared/opd-mockup-bridge.js. In the legacy pages the
// server inlined window.__OPD_DATA__ and this ran once per page load; in the
// SPA, src/lib/bridge.js fetches the bootstrap payload per route and calls
// installBridge(window) again, so install must be idempotent (listeners are
// replaced, not stacked).
// Bearer for the surviving Express legs (requireMedplumBearer routes). The
// token-gated intake submit below deliberately does NOT send it.
import { authHeader, getMedplum } from './medplum-client.js';
import { apiUrl } from './net-base.js';
import { isMedplumUuid } from './medplum-photo.js';
// FHIR-direct Encounter.priority write (the reception "notify doctor" flag) —
// replaces the deleted Express POST /encounters/:id/flag.
// signViaBot — the atomic `sign` Bot (runAsUser) replacing the deleted Express
// POST /encounters/:id/prescription/sign for this legacy click-router path.
import { ensureSignAccessForEncounter, setEncounterPriorityInMedplum, signViaBot, resolveSavedDraftRef } from './medplum-writes.js';
// ONLINE-PRIMARY browser-direct writes replacing two surviving Express legs:
//   • openConsult    — READY→IN_CONSULT (If-Match inside) ← POST …/consult/open
//   • bookAppointment — atomic walk-in booking bundle       ← POST …/scheduling/book
import { openConsult } from './medplum-consult-extras.js';
import { bookAppointment } from './medplum-booking.js';
// The single client-side-nav choke point. navTo falls back to g.location.href
// for non-staff paths / flag off, so the click router below stays byte-identical
// to today under full reload.
import { navTo } from './opd-nav.js';

/** /consult/:id with optional ?patient=<Medplum Patient UUID> for on-open auto-grant. */
export function consultUrl(id, patientId) {
  const base = `/consult/${encodeURIComponent(id)}`;
  if (patientId && isMedplumUuid(patientId)) {
    return `${base}?patient=${encodeURIComponent(patientId)}`;
  }
  return base;
}

// Walk-in queue-token seam for bookAppointment. A small `$mint-token` Bot
// (cfg.bots.mintToken) stamps a sequential per-department daily token atomically
// inside the booking bundle. The Bot is OPTIONAL: if it isn't configured the
// walk-in books with NO token (the desk assigns one at check-in — server parity),
// exactly like the reception dialog without the seam. bookAppointment wraps this
// in try/catch, so a Bot error simply yields a null token and never fails booking.
async function walkInMintToken(deptSlug) {
  const { medplum, cfg } = await getMedplum();
  const botId = cfg && cfg.bots && cfg.bots.mintToken;
  if (!botId) return null;
  // executeBot wraps the handler return inconsistently ({returnValue} vs the value
  // directly) — defensively unwrap both, the established Bot idiom (medplum-writes).
  const out = await medplum.executeBot(botId, { departmentId: deptSlug || null });
  const v = (out && out.returnValue) || out;
  return (v && (typeof v === 'string' ? v : v.token)) || null;
}

export function installBridge(g) {
  const D = g.__OPD_DATA__ || {};
  const meta = D.meta || {};

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // When the bootstrap is live for a given root (encounter/portal/intake/queue/
  // reception), an absent or empty value is the real answer — return a
  // structurally-shaped empty value rather than the demo fallback. We try to
  // mirror the shape of the demo fallback so consumer code doesn't NPE.
  function fallbackForEmpty(fb) {
    if (Array.isArray(fb)) return [];
    if (fb && typeof fb === 'object') {
      // Return a shallow-empty copy: keep keys, empty values.
      const out = {};
      for (const k of Object.keys(fb)) {
        const v = fb[k];
        if (Array.isArray(v)) out[k] = [];
        else if (v && typeof v === 'object') out[k] = {};
        else if (typeof v === 'string') out[k] = '';
        else if (typeof v === 'number') out[k] = null;
        else out[k] = null;
      }
      return out;
    }
    if (typeof fb === 'string') return '';
    return null;
  }

  // STABLE OBJECT IDENTITY + CONTENTS SWAP (design §1b): reuse the existing
  // g.OPDBridge object (if any) and reassign every canonical key onto it, rather
  // than building a fresh object and repointing g.OPDBridge. Any code holding a
  // reference to the bridge OBJECT (the lang pill, the click router, screens
  // that keep `const bridge = window.OPDBridge` at module scope) therefore stays
  // fresh across per-route re-installs. Code that captured a VALUE off it (a
  // .hospital / .currentUser / .pick(...) result) is fixed per-screen. Every key
  // — including hospital/currentUser — is reassigned on every install with a
  // fallback ({} / null), so a route whose payload lacks a section OVERWRITES
  // the previous route's value rather than leaking it. pick/isLive/openWalkInForm
  // are recreated each install so their closures capture the NEW D/meta.
  const Bridge = Object.assign(g.OPDBridge || {}, {
    raw: D,
    meta,
    // — Queue & reception (rows for queue.jsx / reception queue) —
    queue: D.queue || { rows: [], date: null },
    reception: D.reception || { rows: [], date: null },
    // — Encounter view (Screens 6, 6′, 7, 8) —
    encounter: D.encounter || null,
    // — Intake landing/step/review (Screens 1, 2, 3, 10) —
    intake: D.intake || null,
    // — Portal (Screen 4) —
    portal: D.portal || null,
    // — Branding & identity (server-injected; generic fallbacks) —
    hospital: D.hospital || {},
    currentUser: D.currentUser || null,

    /**
     * Return real data if present + sane, else the inline demo fallback.
     * Mockups use this once per top-level const:
     *
     *   const PATIENTS = OPDBridge.pick('queue.rows', [...inline demo]);
     */
    pick(pathStr, fallback) {
      try {
        const parts = pathStr.split('.');
        let v = D;
        // Track whether the root container the path is rooted in is itself
        // live (i.e., bootstrap actually provided that container). If it is
        // live, an empty array/object is a *real* "no data" answer for this
        // patient and we should NOT fall back to the demo content — that
        // would leak Suman's data onto a brand-new patient's page.
        const rootKey = parts[0];
        const isLiveRoot = D && Object.prototype.hasOwnProperty.call(D, rootKey) && D[rootKey] != null;
        for (const p of parts) {
          if (v == null) return isLiveRoot ? fallbackForEmpty(fallback) : fallback;
          v = v[p];
        }
        if (v == null) return isLiveRoot ? fallbackForEmpty(fallback) : fallback;
        if (Array.isArray(v) && v.length === 0) {
          return isLiveRoot ? v : fallback; // live empty = real empty; non-live = demo
        }
        return v;
      } catch (e) { return fallback; }
    },

    /**
     * URL helper. Mockups need to navigate to "another screen" — for example,
     * queue row → consult view. Each canonical screen has a stable URL.
     */
    // SPA routes — the react-router paths replace the legacy /healther/opd/* pages.
    url: {
      consult:        consultUrl,
      consultTablet:  (id) => `/consult-tablet/${encodeURIComponent(id)}`,
      // (url.prescription removed 2026-07-13 with the retired Writer mockup —
      //  prescribing lives in the consult editor at url.consult.)
      paperRx:        (id) => `/consult/${encodeURIComponent(id)}/paper`,
      intake:         (token) => `/intake?token=${encodeURIComponent(token)}`,
      saathi:         (token) => `/saathi?token=${encodeURIComponent(token)}`,   // mobile VOICE intake (same encounter as kiosk)
      intakeStep:     (token, n) => `/intake/step/${n}?token=${encodeURIComponent(token)}`,
      intakeReview:   (token) => `/intake/review?token=${encodeURIComponent(token)}`,
      portal:         (token) => `/portal?token=${encodeURIComponent(token)}`,
      doctorQueue:    ()  => `/queue`,
      receptionQueue: ()  => `/reception`,
      tabletIntake:   (id) => `/tablet-intake/${encodeURIComponent(id)}`,
      // Self-service voice kiosk. Points at /kiosk — the canonical custom-widget
      // kiosk (kiosk-canon/) served as a static directory by Express. A static
      // page that self-gates by phone (no :id) — the patient re-identifies at the gate and
      // the scheduler re-resolves the same encounter, so the set_identity name
      // back-fill runs. (/kiosk-v2 remains a direct ElevenLabs alias.)
      kiosk:          () => `/kiosk`,
    },

    /**
     * Patch a top-level value at runtime (used by demo bookmarks that want
     * to inspect a specific patient without round-tripping).
     */
    set(key, value) { this[key] = value; },

    /**
     * Tells whether the bridge has *real* backend data (vs. just demo).
     */
    isLive() {
      return !!(D.queue || D.reception || D.encounter || D.intake || D.portal);
    },

    /**
     * Slim walk-in patient form: opens a centred modal with name, phone, age,
     * sex, language, doctor + department; books the walk-in BROWSER-DIRECT via
     * medplum-booking's atomic transaction (bookAppointment, startISO:'walk-in')
     * as the logged-in caller, then reloads to show the new row.
     *
     * Used by the reception "Walk-in patient" toolbar button.
     */
    openWalkInForm() {
      if (g.document.getElementById('opd-walkin-modal')) return;
      const overlay = g.document.createElement('div');
      overlay.id = 'opd-walkin-modal';
      overlay.style.cssText = [
        'position:fixed','inset:0','z-index:99998',
        'background:rgba(15,31,44,0.36)',
        'display:flex','align-items:center','justify-content:center',
        'font:500 13px/1.4 Inter,system-ui,sans-serif',
      ].join(';');
      const card = g.document.createElement('div');
      card.style.cssText = [
        'background:white','border-radius:14px','padding:24px',
        'width:420px','max-width:92vw','box-shadow:0 24px 64px rgba(15,31,44,0.32)',
      ].join(';');
      const now = new Date(Date.now() + 5 * 60 * 1000);
      card.innerHTML = `
        <div style="font:700 18px/1.2 Inter,system-ui,sans-serif;color:#0F1F2C;margin-bottom:6px">Walk-in patient</div>
        <div style="color:#7A8896;margin-bottom:16px">Creates a new appointment for now + 5min.</div>
        <form id="opd-walkin-form" style="display:grid;gap:10px">
          <label style="display:grid;gap:4px"><span style="color:#4A5968;font-weight:600;font-size:11.5px;letter-spacing:.04em">Name</span>
            <input name="name" required placeholder="Patient name" style="height:36px;padding:0 10px;border:1px solid #CDD5DD;border-radius:8px;font:500 13px Inter"/></label>
          <div style="display:grid;grid-template-columns:1fr 80px 80px;gap:10px">
            <label style="display:grid;gap:4px"><span style="color:#4A5968;font-weight:600;font-size:11.5px;letter-spacing:.04em">Phone</span>
              <input name="phone" placeholder="+91 9XXXX XXXXX" style="height:36px;padding:0 10px;border:1px solid #CDD5DD;border-radius:8px;font:500 13px Inter"/></label>
            <label style="display:grid;gap:4px"><span style="color:#4A5968;font-weight:600;font-size:11.5px;letter-spacing:.04em">Age</span>
              <input name="age" type="number" min="0" max="120" style="height:36px;padding:0 10px;border:1px solid #CDD5DD;border-radius:8px;font:500 13px Inter"/></label>
            <label style="display:grid;gap:4px"><span style="color:#4A5968;font-weight:600;font-size:11.5px;letter-spacing:.04em">Sex</span>
              <select name="sex" style="height:36px;padding:0 8px;border:1px solid #CDD5DD;border-radius:8px;font:500 13px Inter">
                <option value="F">F</option><option value="M">M</option><option value="O">O</option>
              </select></label>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
            <label style="display:grid;gap:4px"><span style="color:#4A5968;font-weight:600;font-size:11.5px;letter-spacing:.04em">Doctor</span>
              <input name="doctor_name" value="${escapeHtml((Bridge.currentUser && Bridge.currentUser.name) || '')}" placeholder="Doctor name" style="height:36px;padding:0 10px;border:1px solid #CDD5DD;border-radius:8px;font:500 13px Inter"/></label>
            <label style="display:grid;gap:4px"><span style="color:#4A5968;font-weight:600;font-size:11.5px;letter-spacing:.04em">Department</span>
              <input name="department" value="General Medicine" style="height:36px;padding:0 10px;border:1px solid #CDD5DD;border-radius:8px;font:500 13px Inter"/></label>
          </div>
          <input type="hidden" name="doctor_id" value="${escapeHtml((Bridge.currentUser && Bridge.currentUser.id) || '')}"/>
          <input type="hidden" name="language" value="hi"/>
          <input type="hidden" name="appointment_iso" value="${now.toISOString()}"/>
          <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:6px">
            <button type="button" data-opd-skip-route data-cancel style="height:36px;padding:0 14px;border-radius:8px;border:1px solid #CDD5DD;background:white;color:#0F1F2C;cursor:pointer;font-weight:600">Cancel</button>
            <button type="submit" data-opd-skip-route style="height:36px;padding:0 14px;border-radius:8px;border:none;background:#0F6D8C;color:white;cursor:pointer;font-weight:600">Create appointment</button>
          </div>
        </form>
      `;
      overlay.appendChild(card);
      g.document.body.appendChild(overlay);
      function close() { try { overlay.remove(); } catch (e) {} }
      card.querySelector('[data-cancel]').addEventListener('click', close);
      overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });
      const form = card.querySelector('#opd-walkin-form');
      form.addEventListener('submit', async (ev) => {
        ev.preventDefault();
        const fd = new FormData(form);
        const f = Object.fromEntries(fd.entries());
        try {
          // ONLINE-PRIMARY: book the walk-in BROWSER-DIRECT as the logged-in caller
          // (RLS-scoped by their own token) through medplum-booking's ONE atomic
          // Patient/Appointment/Encounter transaction — replacing the Express POST
          // /scheduling/book leg. startISO:'walk-in' checks the patient in now; the
          // token rides the bundle when the optional $mint-token Bot is configured
          // (walkInMintToken → cfg.bots.mintToken), else null (assigned at check-in).
          // This bridge fallback is online-only exactly as the old fetch was — it
          // never used the offline outbox, so no offline capability is lost (the
          // reception dialog keeps its own outbox walk-in path unchanged).
          const j = await bookAppointment({
            patient: {
              name: f.name,
              phone: f.phone || undefined,
              age: f.age ? Number(f.age) : undefined,
              sex: f.sex || undefined,
            },
            doctorId: f.doctor_id || undefined,
            doctorName: f.doctor_name || undefined,
            // The form carries a department display name; the scheduler keys on the
            // id slug ('General Medicine' → 'general-medicine').
            departmentId: String(f.department || '').trim().toLowerCase().replace(/\s+/g, '-') || undefined,
            departmentName: f.department || undefined,
            startISO: 'walk-in',
            source: 'reception',
          }, { mintToken: walkInMintToken });
          // Row-flash keys on the queue rows' slug ids; `encounterId` is the
          // Medplum UUID now (online-primary) — prefer the explicit slug.
          const newId = j.encounterSlug || (j.encounter && j.encounter.id) || j.encounterId;
          showToast('Walk-in added · ' + f.name + (j.token ? ' · token ' + j.token : ''), 'ok');
          close();
          // Stash the new id so the next page-load can scroll/flash it.
          try { g.sessionStorage.setItem('opd-walkin-flash', newId || ''); } catch (e) {}
          setTimeout(() => g.location.reload(), 600);
        } catch (e) { showToast('Walk-in failed: ' + e.message, 'err'); }
      });
    },
  });

  // — i18n: a single in-header language toggle for every mockup that
  //   accepts a `lang` / `onLang` prop. Initial value comes from URL
  //   ?lang=… → localStorage → 'en'. Persistence via localStorage.
  const LANG_KEY = 'healther.opd.lang';
  function readInitialLang() {
    const q = new URLSearchParams(g.location.search).get('lang');
    if (q === 'hi' || q === 'en') return q;
    try {
      const ls = g.localStorage.getItem(LANG_KEY);
      if (ls === 'hi' || ls === 'en') return ls;
    } catch (e) {}
    return (meta.lang === 'hi') ? 'hi' : 'en';
  }
  Bridge.lang = readInitialLang();
  Bridge.setLang = function (v) {
    if (v !== 'en' && v !== 'hi') return;
    Bridge.lang = v;
    try { g.localStorage.setItem(LANG_KEY, v); } catch (e) {}
    g.document?.documentElement?.setAttribute('data-lang', v);
    g.dispatchEvent(new CustomEvent('opd:lang', { detail: { lang: v } }));
  };
  // Apply initial lang to document for any CSS-based switching.
  try { g.document.documentElement.setAttribute('data-lang', Bridge.lang); } catch (e) {}

  // Tiny global EN / हिं pill. Only rendered if the mockup hasn't already
  // placed its own toggle (signaled via <body data-opd-langtoggle="off">).
  // Position: inside the page's natural top-right gutter, NOT overlapping
  // header chrome. We compute a top inset of 12px below the top of the
  // first <header> if present, else default 14px from the viewport top.
  // SPA: install usually runs after DOMContentLoaded — render immediately when
  // the document is ready; the getElementById guard keeps re-installs no-ops.
  const renderLangToggle = () => {
    // The patient Summary is intentionally bilingual on every card. Do not
    // present a language control that cannot actually switch that content; also
    // remove a toggle left behind by an in-app navigation from another route.
    if (meta.route === 'portal') {
      g.document.getElementById('opd-lang-toggle')?.remove();
      return;
    }
    if (g.document.body?.getAttribute('data-opd-langtoggle') === 'off') return;
    if (g.document.getElementById('opd-lang-toggle')) return;
    const el = g.document.createElement('div');
    el.id = 'opd-lang-toggle';
    el.setAttribute('role', 'group');
    el.setAttribute('aria-label', 'Language toggle');
    el.style.cssText = [
      // Bottom-right so it never overlaps a page's top header / patient name /
      // toolbar. Surfaces that want it inline opt out via data-opd-langtoggle="off".
      'position:fixed', 'bottom:16px', 'right:16px', 'z-index:9999',
      'display:inline-flex', 'gap:2px',
      'background:rgba(255,255,255,0.92)', 'backdrop-filter: blur(8px)',
      'border:1px solid rgba(15,31,44,0.08)',
      'box-shadow:0 1px 4px rgba(15,31,44,0.06)',
      'padding:3px', 'border-radius:999px',
      'font:600 11.5px/1 Inter,system-ui,sans-serif', 'letter-spacing:0.02em',
    ].join(';');
    function btn(label, val) {
      const b = g.document.createElement('button');
      b.type = 'button';
      b.textContent = label;
      b.dataset.lang = val;
      b.style.cssText = 'background:transparent;border:0;cursor:pointer;padding:5px 10px;border-radius:999px;color:#374151';
      // Route through g.OPDBridge so the pill keeps working across SPA
      // re-installs (this closure outlives the Bridge that created it).
      b.onclick = () => (g.OPDBridge || Bridge).setLang(val);
      return b;
    }
    const en = btn('EN', 'en');
    const hi = btn('हिं', 'hi');
    function apply() {
      const active = (g.OPDBridge || Bridge).lang;
      for (const b of [en, hi]) {
        const on = b.dataset.lang === active;
        b.style.background = on ? '#0f766e' : 'transparent';
        b.style.color = on ? 'white' : '#374151';
      }
    }
    apply();
    g.addEventListener('opd:lang', apply);
    el.appendChild(en); el.appendChild(hi);
    g.document.body.appendChild(el);
  };
  if (g.document.readyState === 'loading') {
    g.addEventListener('DOMContentLoaded', renderLangToggle);
  } else {
    renderLangToggle();
  }

  // Tiny error surface so the diagnostic page can show what's missing.
  if (D.error) {
    console.warn('[OPDBridge] backend payload returned error:', D.error);
  }

  // ── Global click router for the design-system mockups ──────────────
  //
  // The mockups have buttons like "Start consult", "Shuru karein", "Submit",
  // "Resend link" without onClick handlers — they were rendered as static
  // demos. Rather than edit every JSX file to add navigation, we listen at
  // the document level and route by button label (and, when available, by a
  // closest enclosing row data attribute). Everything is best-effort; if a
  // button doesn't match a known label, the click does nothing (same as
  // before).
  //
  // Recognised labels and where they go:
  //   "Start consult" / "Shuru karein" / "Start" → consult or step/1
  //   "Continue" / "Next" / "Aage badhein"        → next step / review
  //   "Submit" / "Bhejein"                        → portal (after POST)
  //   "Resend link" / "Send link"                 → POST resend-link
  //   "Open as patient" / "Open"                  → intake landing
  //
  // The handler reads the meta + bridge data to know the current encounterId
  // and token, plus uses the nearest [data-encounter-id] / [data-token] /
  // [data-row-id] on an ancestor when set.

  function findUpRowId(el) {
    let n = el;
    while (n && n.nodeType === 1) {
      if (n.dataset?.encounterId) return n.dataset.encounterId;
      if (n.dataset?.rowId) return n.dataset.rowId;
      n = n.parentElement;
    }
    return null;
  }

  function showToast(message, tone) {
    let t = g.document.getElementById('opd-toast');
    if (!t) {
      t = g.document.createElement('div');
      t.id = 'opd-toast';
      t.style.cssText = [
        'position:fixed','left:50%','bottom:24px','transform:translateX(-50%)',
        'z-index:99999','padding:10px 18px','border-radius:999px',
        'font:500 13px/1 Inter,system-ui,sans-serif',
        'box-shadow:0 4px 24px rgba(15,31,44,0.18)',
        'transition:opacity 0.2s',
      ].join(';');
      g.document.body.appendChild(t);
    }
    t.style.background = tone === 'err' ? '#fef2f2' : (tone === 'ok' ? '#ecfdf5' : '#0f172a');
    t.style.color = tone === 'err' ? '#991b1b' : (tone === 'ok' ? '#065f46' : 'white');
    t.style.border = tone === 'err' ? '1px solid #fecaca' : (tone === 'ok' ? '1px solid #a7f3d0' : 'none');
    t.textContent = message;
    t.style.opacity = '1';
    clearTimeout(t._timer);
    t._timer = setTimeout(() => { t.style.opacity = '0'; }, 3200);
  }

  function currentEncounterId() {
    return meta.encounterId
      || (Bridge.encounter?.encounter?.id)
      || null;
  }
  function currentToken() {
    return meta.token || null;
  }
  function currentStep() {
    const m = (g.location.pathname.match(/\/intake\/step\/(\d+)/) || [])[1];
    return m ? parseInt(m, 10) : null;
  }

  // Replace (never stack) the click router across SPA bridge re-installs, so
  // the handler always closes over the current Bridge/meta.
  if (g.__opdClickRouter) g.removeEventListener('click', g.__opdClickRouter, true);
  g.__opdClickRouter = async (ev) => {
    const target = ev.target.closest('button, a, [role="button"], [data-action]');
    if (!target) return;
    // Don't hijack anchors that already have an href.
    if (target.tagName === 'A' && target.getAttribute('href') && target.getAttribute('href') !== '#') return;
    // Don't hijack the language toggle pill or any explicit opt-out.
    if (target.closest('#opd-lang-toggle, [data-opd-skip-route]')) return;

    const action = (target.dataset?.action || '').toLowerCase();
    const labelRaw = (target.textContent || '').trim();
    const label = labelRaw.toLowerCase();
    const rowId = findUpRowId(target);
    const eid = rowId || currentEncounterId();
    const token = currentToken();

    function navigateTo(url) {
      if (!url) return;
      ev.preventDefault();
      // navTo itself falls back to g.location.href for non-staff paths / flag
      // off, so this stays byte-identical to today under full reload. We do NOT
      // add a blanket anchor interceptor here — real anchors are converted at
      // their source via linkProps (design §1b); the anchor-skip below stays.
      navTo(url);
    }

    // Patient intake start
    if (action === 'intake:start' || /^(shuru karein|start|शुरू|शुरू करें|begin|chaliye shuru|let'?s? start)\b/.test(label)) {
      if (token) return navigateTo(Bridge.url.intakeStep(token, 1));
    }
    // Intake next step
    if (action === 'intake:next' || /^(next|aage|aage badhein|continue|आगे|aage badho)\b/.test(label)) {
      if (token) {
        const n = currentStep();
        if (n && n < 5) return navigateTo(Bridge.url.intakeStep(token, n + 1));
        if (n === 5) return navigateTo(Bridge.url.intakeReview(token));
      }
    }
    // Intake → Review
    if (action === 'intake:review' || /^(review|review my intake|samiksha)\b/.test(label)) {
      if (token) return navigateTo(Bridge.url.intakeReview(token));
    }
    // Intake → Submit
    if (action === 'intake:submit' || /^(submit|bhejein|jama karein|send|send to doctor|submit early)\b/.test(label)) {
      if (token) {
        try {
          const r = await fetch(apiUrl(`/api/v1/opd/encounters/${eid}/intake/submit?token=${encodeURIComponent(token)}`), {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
          });
          if (r.ok) showToast('Submitted — your doctor will see this shortly', 'ok');
          else showToast('Submit failed (' + r.status + ') — see Diagnostic page', 'err');
        } catch (e) { showToast('Submit failed: ' + e.message, 'err'); }
        return ev.preventDefault();
      }
    }
    // Doctor: start / open consult (also handle reception "View intake summary")
    if (action === 'doctor:start' || /^(start consult|open consult|view consult|view intake summary|view intake|consult)\b/.test(label)) {
      if (eid) {
        // Best-effort READY→IN_CONSULT transition before navigating — browser-direct
        // as the logged-in doctor (RLS-scoped, If-Match optimistic concurrency inside)
        // via medplum-consult-extras, replacing the Express POST …/consult/open. Only
        // a real Medplum UUID can be read/transitioned; a legacy slug row just navigates
        // (the transition is best-effort, exactly as the old fetch's try/catch was).
        if (isMedplumUuid(eid)) {
          try { await openConsult({ encounterId: eid, actor: (Bridge.currentUser && Bridge.currentUser.name) || 'Doctor' }); } catch (e) {}
        }
        return navigateTo(Bridge.url.consult(eid));
      }
    }
    // Legacy prescription actions now open the canonical consult editor. The
    // standalone Writer was retired; Bridge.url.prescription no longer exists.
    if (action === 'doctor:rx' || /^(open prescription writer|prescription|rx writer)\b/.test(label)) {
      if (eid) return navigateTo(Bridge.url.consult(eid));
    }
    // Doctor: open paper Rx flow
    if (action === 'doctor:paper' || /^(photo of paper prescription|photograph paper prescription|paper prescription|📷)/.test(label)) {
      if (eid) return navigateTo(Bridge.url.paperRx(eid));
    }
    // Doctor: sign & send
    if (action === 'doctor:sign' || /^(sign|sign & send|sign and send|approve & sign|approve and sign)\b/.test(label)) {
      if (eid) {
        // ONLINE-PRIMARY: sign via the proven `sign` Bot (atomic transaction,
        // runAsUser) — Medplum-ONLY. Sign-by-REFERENCE (the 3169e05 contract):
        // the Bot signs only the SAVED consult-draft Composition, so resolve
        // that draft's exact id/version AS THE CALLER and point the Bot at it —
        // never send clinical bytes from the bridge (a bridge pick can be
        // stale, and the Bot ignores payload drafts anyway). No saved draft →
        // an honest toast; already-signed (draft frozen 'final') → say so.
        // Best-effort click-router semantics preserved: a Bot allergy block or a
        // failed sign surfaces a toast, never throws out of the handler.
        try {
          await ensureSignAccessForEncounter(eid);
          const draftRef = await resolveSavedDraftRef(eid);
          if (!draftRef) {
            showToast('No saved draft to sign — open the consult and save the draft first', 'err');
            return ev.preventDefault();
          }
          if (draftRef.status === 'final') {
            showToast('Already signed — this visit has a signed prescription', 'ok');
            return ev.preventDefault();
          }
          // resolveSavedDraftRef carries the saved draft's medCount so the
          // click-router (which does not hold the draft object) still arms
          // signViaBot's stale-Bot empty-Rx tripwire.
          const out = await signViaBot({ encounterId: eid, payload: {}, draftRef, expectedMedCount: (typeof draftRef.medCount === 'number' ? draftRef.medCount : null) });
          if (out && out.blocked) {
            showToast('Safety check: review the allergy alert before signing', 'err');
          } else if (out && (out.signed || out.alreadySigned)) {
            showToast('Signed · sent to patient via magic link', 'ok');
            setTimeout(() => navigateTo('/queue'), 1200);
          } else {
            showToast('Sign failed' + (out && out.error ? ' (' + out.error + ')' : ''), 'err');
          }
        } catch (e) { showToast('Sign failed: ' + (e && e.message || e), 'err'); }
        return ev.preventDefault();
      }
    }
    // Reception: open patient's magic link in a new tab. Match BEFORE the
    // resend matcher because "Open in new tab" must not fall through to
    // anything else.
    if (action === 'reception:open-link' || /^(open in new tab|open patient link|preview as patient)\b/.test(label)) {
      if (eid) {
        ev.preventDefault();
        ev.stopPropagation();
        let url = null;
        try {
          const rows = (Bridge.reception?.rows) || [];
          const row = rows.find((x) => x.encounterId === eid || x.id === eid);
          url = row?.linkUrl || null;
        } catch (e) {}
        if (!url) {
          try {
            const r = await fetch(apiUrl(`/api/v1/opd/encounters/${eid}/resend-link`), {
              method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: '{}',
            });
            if (r.ok) { const j = await r.json(); url = j.link || null; }
          } catch (e) {}
        }
        if (url) g.open(url, '_blank', 'noopener');
        else showToast('No magic link available yet', 'err');
        return;
      }
    }
    // Reception: walk-in (creates a brand-new appointment for now+5min)
    if (action === 'reception:walkin' || /^(walk-?in patient|new walk-?in|\+ ?walk-?in)\b/.test(label)) {
      ev.preventDefault();
      ev.stopPropagation();
      try {
        if (typeof Bridge.openWalkInForm === 'function') Bridge.openWalkInForm();
      } catch (e) { showToast('Walk-in failed: ' + e.message, 'err'); }
      return;
    }
    // Reception/Doctor: soft-priority flag — FHIR-direct Encounter.priority
    // write as the logged-in caller (RLS-scoped); the old Express /flag route
    // is deleted. Fail loud in the toast.
    if (action === 'reception:priority' || /^(notify (reception|doctor)|mark priority|flag priority)\b/.test(label)) {
      if (eid) {
        ev.preventDefault();
        ev.stopPropagation();
        try {
          await setEncounterPriorityInMedplum(eid);
          showToast('Marked priority', 'ok');
        } catch (e) { showToast('Flag failed: ' + e.message, 'err'); }
        return;
      }
    }
    // Reception: hand tablet at desk — match BEFORE resend (the
    // mockup's "Or hand tablet at reception" button text contains the
    // word "tablet" and otherwise the resend regex would not catch it
    // anyway, but keep ordering explicit).
    if (action === 'reception:tablet' || /^(hand tablet|tablet at reception|reception tablet|or hand tablet)\b/.test(label)) {
      return navigateTo(Bridge.url.kiosk());   // new voice kiosk (was legacy /tablet-intake/:id)
    }
    // Reception: send this walk-in to the self-service kiosk.
    if (action === 'reception:kiosk' || /^(send to kiosk|kiosk|to kiosk)\b/.test(label)) {
      return navigateTo(Bridge.url.kiosk());   // /kiosk-v2 — was legacy /kiosk/:id
    }
    // Reception: send the patient to MOBILE voice intake (/saathi) on their own phone.
    // Same magic-link token as the kiosk → same encounter (concurrency: hop kiosk↔mobile).
    if (action === 'reception:mobile-intake' || /^(send to mobile intake|mobile intake|saathi link|voice intake link)\b/.test(label)) {
      if (eid) {
        ev.preventDefault(); ev.stopPropagation();
        let tok = null;
        try { const row = (Bridge.reception?.rows || []).find((x) => x.encounterId === eid || x.id === eid); tok = row?.token || null; } catch (e) {}
        if (!tok) {
          try {
            const r = await fetch(apiUrl(`/api/v1/opd/encounters/${eid}/resend-link`), { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: '{}' });
            if (r.ok) { const j = await r.json(); tok = j.token || null; }
          } catch (e) {}
        }
        if (tok) {
          const url = Bridge.url.saathi(tok);
          try { await g.navigator?.clipboard?.writeText(g.location.origin + url); } catch (e) {}
          showToast('Mobile voice-intake link sent on WhatsApp · link copied', 'ok');
        } else { showToast('No magic link available yet', 'err'); }
        return;
      }
    }
    // Reception: resend WhatsApp link — stays on the thin Express gateway
    // (magic-link token minting is server-side), now with the Medplum bearer.
    if (action === 'reception:resend' || /^(resend(?: link)?|resend whatsapp link|whatsapp link|send link)\b/.test(label)) {
      if (eid) {
        try {
          const r = await fetch(apiUrl(`/api/v1/opd/encounters/${eid}/resend-link`), {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: '{}',
          });
          if (r.ok) {
            const j = await r.json();
            showToast('Link resent · just now', 'ok');
            // Update the row's last-sent timestamp pill if present.
            try {
              const row = target.closest('[data-encounter-id], [data-row-id]');
              const stamp = row?.querySelector('[data-link-last-sent]');
              if (stamp) stamp.textContent = 'Link sent just now';
            } catch (e) {}
          } else { showToast('Resend failed (' + r.status + ')', 'err'); }
        } catch (e) { showToast('Resend failed: ' + e.message, 'err'); }
        return ev.preventDefault();
      }
    }
    // Doctor queue row click — any clickable row gets a default to consult.
    if (rowId && !action && /(patient|row)/.test(target.className || '')) {
      return navigateTo(Bridge.url.consult(rowId));
    }
    // Otherwise, do nothing — let the click pass through.
  };
  g.addEventListener('click', g.__opdClickRouter, true); // capture phase — runs before any JSX handler

  g.OPDBridge = Bridge;
  return Bridge;
}
